import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertCircle, CheckCircle2, Clock3 } from "lucide-react";

import api from "../lib/api";

function getStatusTone(status) {
  if (status === "success") {
    return {
      icon: CheckCircle2,
      title: "Payment received",
      text: "Your appointment request has been received and marked for confirmation.",
      color: "text-[#079b65]",
      bg: "bg-[#e8fff6]",
    };
  }

  if (status === "failed" || status === "error") {
    return {
      icon: AlertCircle,
      title: "Payment was not completed",
      text: "Your appointment is still pending payment. You can try booking again or contact us for help.",
      color: "text-[#dc2626]",
      bg: "bg-[#fff1f2]",
    };
  }

  return {
    icon: Clock3,
    title: "Payment status pending",
    text: "We could not confirm a final payment status yet. Please contact us if you completed payment.",
    color: "text-[#06386f]",
    bg: "bg-[#edf4ff]",
  };
}

export default function PaymentResultPage() {
  const [searchParams] = useSearchParams();
  const [saved, setSaved] = useState(false);
  const status = String(searchParams.get("status") || "pending").toLowerCase();
  const failureReason =
    searchParams.get("reasonDescription") ||
    searchParams.get("reason") ||
    searchParams.get("message") ||
    "";
  const tone = useMemo(() => getStatusTone(status), [status]);
  const Icon = tone.icon;

  useEffect(() => {
    const payload = Object.fromEntries(searchParams.entries());

    if (!payload.order_id) {
      setSaved(true);
      return;
    }

    api.post("/payments/wipay-return", payload).finally(() => setSaved(true));
  }, [searchParams]);

  return (
    <section className="bg-[#f5f8ff] px-5 py-24 md:py-32">
      <div className="mx-auto max-w-[760px] rounded-[14px] bg-white p-8 text-center shadow-[0_22px_70px_rgba(6,56,111,0.14)] md:p-12">
        <div className={`mx-auto grid h-20 w-20 place-items-center rounded-full ${tone.bg}`}>
          <Icon className={`h-10 w-10 ${tone.color}`} />
        </div>
        <h1 className="mt-7 text-[34px] font-extrabold leading-tight text-[#071746] md:text-[44px]">
          {tone.title}
        </h1>
        <p className="mx-auto mt-4 max-w-[560px] text-[17px] leading-relaxed text-[#4d5b7c]">
          {tone.text}
        </p>
        {failureReason ? (
          <p className="mx-auto mt-3 max-w-[560px] rounded-[10px] bg-[#fff7ed] px-4 py-3 text-[14px] font-semibold leading-relaxed text-[#9a3412]">
            WiPay message: {failureReason}
          </p>
        ) : status !== "success" ? (
          <p className="mx-auto mt-3 max-w-[560px] rounded-[10px] bg-[#fff7ed] px-4 py-3 text-[14px] font-semibold leading-relaxed text-[#9a3412]">
            Sandbox tip: use an approved test card and a valid-looking Jamaica phone number, for example 8767295735.
          </p>
        ) : null}

        <div className="mt-8 rounded-[10px] border border-[#e2e8f0] bg-[#fbfdff] p-4 text-left text-[14px] text-[#4d5b7c]">
          <div><strong className="text-[#071746]">Status:</strong> {status}</div>
          {searchParams.get("transaction_id") ? (
            <div className="mt-2 break-all"><strong className="text-[#071746]">Transaction:</strong> {searchParams.get("transaction_id")}</div>
          ) : null}
          {searchParams.get("order_id") ? (
            <div className="mt-2 break-all"><strong className="text-[#071746]">Appointment:</strong> {searchParams.get("order_id")}</div>
          ) : null}
          {searchParams.get("total") ? (
            <div className="mt-2"><strong className="text-[#071746]">Total:</strong> {searchParams.get("currency") || "JMD"} {searchParams.get("total")}</div>
          ) : null}
          {failureReason ? (
            <div className="mt-2 break-words"><strong className="text-[#071746]">WiPay message:</strong> {failureReason}</div>
          ) : null}
        </div>

        <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
          <Link to="/" className="inline-flex h-12 items-center justify-center rounded-[10px] bg-[#0052ff] px-7 font-bold text-white transition hover:bg-[#003ec2]">
            Back to Home
          </Link>
          <Link to="/book" className="inline-flex h-12 items-center justify-center rounded-[10px] border border-[#aeb3bd] px-7 font-bold text-[#071746] transition hover:border-[#0052ff] hover:text-[#0052ff]">
            Book Another Appointment
          </Link>
        </div>

        {!saved ? <p className="mt-4 text-xs text-[#4d5b7c]">Saving payment status...</p> : null}
      </div>
    </section>
  );
}
